import clsx from "clsx";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { LogoWhiteOnBlack, Carrot } from "../icons";
import { SearchTakeover } from "../search";
import NavOrExternalLink from "../shared/NavOrExternalLink";
import NavWrapper from "./NavWrapper";
import { NavLinkContentful } from "./NavContentful";

const convertLabelToId = (label: string) => {
  return label
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]/g, "");
};

const heading18 = "text-18 text-white uppercase leading-26";

// Component for individual header links
function HeaderLink({
  header,
  parentUrl,
  isOpen,
  onClose,
}: {
  header: { url: string; label: string };
  parentUrl: string;
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <>
      {header.url ? (
        <NavOrExternalLink
          to={`${parentUrl}${header.url}`}
          className={`cta relative block overflow-hidden ${heading18}`}
          prefetch="intent"
          onClick={onClose}
          aria-hidden={!isOpen}
          tabIndex={isOpen ? 0 : -1}
        >
          {/* Text container with consistent width */}
          <div className="flex items-center">
            {/* Text and right arrow with transform for smooth animation */}
            <div className="ease-out flex items-center transition-transform duration-500">
              <span className="border-b border-white">{header.label}</span>
            </div>
          </div>
        </NavOrExternalLink>
      ) : (
        <div className={heading18}>{header.label}</div>
      )}
    </>
  );
}

// Component for navigation description section
function NavDescription({
  descriptionHeader,
  descriptionText,
}: {
  descriptionHeader?: string;
  descriptionText?: React.ReactNode;
}) {
  return (
    <div className="col-span-3">
      <span className="ppe block text-2xl">{descriptionHeader}</span>
      {descriptionText && (
        <div className="aeonik mt-[22px] block text-18">{descriptionText}</div>
      )}
    </div>
  );
}

// Component for the dropdown content area
function DropdownContent({
  link,
  isOpen,
  onClose,
}: {
  link: NavLinkContentful;
  isOpen: boolean;
  onClose: () => void;
}) {
  return (
    <div className="w-full pt-10 pb-20">
      {/* 5-columns: Description (span-3) + 4 Headers (span-2 each) */}
      <div className="tbi-grid w-full">
        {/* Column 1: Description (span-3) */}
        <NavDescription
          descriptionHeader={link.descriptionHeader}
          descriptionText={link.descriptionText}
        />

        {/* Dynamic Headers: Positioned using col-start, max 4 headers */}
        {link.headersCollection.map((header, index) => {
          const headerSpan =
            link.headersCollection.length === 1 ? "col-span-8" : "col-span-3";
          const hasContent =
            header.linkCollection && header.linkCollection.length > 0;
          // Calculate column start: Start at column 5 for breathing space from description
          const colStart = 5 + index * 3;
          return (
            <div
              key={`header-${header.url}-${index}`}
              className={`${headerSpan} min-h-[24px]`}
              style={{ gridColumnStart: colStart }}
            >
              <HeaderLink
                header={header}
                parentUrl={""}
                isOpen={isOpen}
                onClose={onClose}
              />

              {/* Sublinks Content */}
              {hasContent && (
                <div className="mt-[22px]">
                  {header.linkCollection!.map((sublink) => (
                    <NavOrExternalLink
                      to={sublink.url}
                      className={clsx(
                        "body mt-[10px] block w-full text-white transition-colors duration-300 hover:underline",
                      )}
                      key={sublink.label}
                      prefetch="intent"
                      onClick={onClose}
                      aria-hidden={!isOpen}
                      tabIndex={isOpen ? 0 : -1}
                    >
                      {sublink.label}
                    </NavOrExternalLink>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function DesktopNavContentful({
  links,
}: {
  links: NavLinkContentful[];
}) {
  const [subNav, setSubNav] = useState<string | null>(null);

  return (
    <NavWrapper id="top-nav-desktop" className="lgmax:hidden" isContentfulNav>
      <div
        className={clsx("group/nav z-20 h-24 w-full text-white duration-300")}
      >
        <div className="global-container flex h-full w-full items-center justify-end">
          <NavOrExternalLink
            className="flex-center ml-0 mr-auto h-full transition-opacity hover:opacity-60"
            to="/"
            prefetch="intent"
            tabIndex={0}
          >
            <span className="aeonik flex items-center text-14 uppercase">
              <LogoWhiteOnBlack className="mr-3" id="desktop_logo_white" />
              <span className="relative top-[2px]">
                Tony Blair Institute for Global Change
              </span>
            </span>
          </NavOrExternalLink>
          {/* PRIMARY NAVIGATION LINKS */}
          {links.map((link, idx) => {
            const linkId = convertLabelToId(link.title || `item-${idx}`);
            const hasDropdown =
              (link.headersCollection && link.headersCollection.length > 0) ||
              link.descriptionHeader ||
              link.descriptionText;
            return (
              <div
                className="flex-center h-full"
                key={link.title}
                id={`nav-item-${linkId}`}
              >
                <div
                  onMouseEnter={() => setSubNav(linkId)}
                  onMouseLeave={() => setSubNav(null)}
                  onFocus={() => setSubNav(linkId)}
                  onBlur={() => setSubNav(null)}
                  className={clsx(
                    "group relative z-10 cursor-default text-14",
                    idx !== 0 && "ml-6",
                  )}
                  id={`nav-dropdown-trigger-${linkId}`}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (["Space", "Enter"].includes(e.code)) {
                      setSubNav(linkId);
                    }

                    if (e.code === "Escape") {
                      setSubNav(null);
                    }
                  }}
                >
                  <div className="aeonik flex h-full cursor-pointer flex-col items-center justify-center p-4 text-14 uppercase">
                    <div className="flex items-center">
                      <span>{link.title}</span>
                      <span className="inline-block transition-transform group-hover:rotate-180">
                        <Carrot />
                      </span>
                    </div>
                    <div className="flex w-full origin-center scale-x-0 border-b-2 border-solid border-white transition-transform duration-300 group-hover:scale-x-100 group-focus:scale-x-100" />
                  </div>

                  <div className="absolute hidden h-[34px] w-full bg-transparent group-hover:block group-focus:block" />
                  <div
                    // top 96px minus 2px so there's no gap + 50px social strip height
                    className={clsx(
                      "fixed left-0 max-h-0 w-screen overflow-hidden bg-black text-white transition-[max-height] duration-300",
                      {
                        "max-h-[800px]": subNav === linkId,
                      },
                    )}
                  >
                    {hasDropdown && (
                      <DropdownContent
                        link={link}
                        isOpen={subNav === linkId}
                        onClose={() => setSubNav(null)}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
          <SearchTakeover />

          <AnimatePresence>
            {subNav && (
              <motion.div
                key="blurzone"
                // Height is screen minus nav height minus menu height + 50px social strip height
                className="fixed left-0 bottom-0 z-0 h-[calc(100vh-146px)] w-screen bg-black/90"
                initial={{
                  opacity: 0,
                }}
                animate={{
                  opacity: 1,
                }}
                exit={{
                  opacity: 0,
                }}
                transition={{
                  duration: 0.2,
                }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </NavWrapper>
  );
}
